//@ts-check

/**
 * @fileoverview paymentDataBuilder - Service for building payment data objects
 *
 * @description
 * Extracts payment data building logic from PaymentForm component.
 * Handles building payment objects for Multipass (TSEP), Paytrace, and ACH payments.
 */

import FlexLib from '@flex/lib';
import commonStrings from '../constants/commonStrings';

/** @typedef {import('../../../types/paymentDataBuilder').CardData} CardData */
/** @typedef {import('../../../types/paymentDataBuilder').TsepResponseData} TsepResponseData */
/** @typedef {import('../../../types/paymentDataBuilder').BinCodesResponseData} BinCodesResponseData */
/** @typedef {import('../../../types/paymentDataBuilder').AchData} AchData */
/** @typedef {import('../../../types/paymentDataBuilder').PaymentMethod} PaymentMethod */
/** @typedef {import('../../../types/paymentDataBuilder').PaymentData} PaymentData */
/** @typedef {import('../../../types/paymentDataBuilder').PaymentBuilderDependencies} PaymentBuilderDependencies */

const { maskPaymentValue } = FlexLib.utils.common;

/** @constant {Record<string, number>} Tab indices for payment method selection */
const PAYMENT_TABS = {
  CARD: 0,
  ACH: 1,
};

/** @constant {string} Form selector for Paytrace encryption */
const PAYTRACE_FORM_SELECTOR = '#DemoForm';

/** @constant {string[]} localStorage keys for encrypted payment data */
const ENCRYPTED_DATA_KEYS = ['0', '1'];

/** @constant {string} Error message for card encryption failure */
const ENCRYPTION_ERROR_MESSAGE = 'Card encryption failed. Please try again.';

/**
 * @description Clear encrypted payment data from localStorage
 * @returns {void}
 */
export const clearEncryptedCardData = () => {
  ENCRYPTED_DATA_KEYS.forEach((key) => localStorage.removeItem(key));
};

/**
 * @description Clear encrypted payment data from localStorage
 * @returns {{encryptedCard: string, encryptedCSC: string}} Object containing encrypted card data from localStorage
 */
const getEncryptedCardData = () => {
  return {
    encryptedCard: localStorage.getItem(ENCRYPTED_DATA_KEYS[0]) || '',
    encryptedCSC: localStorage.getItem(ENCRYPTED_DATA_KEYS[1]) || '',
  };
};

/**
 * @description Build Multipass (TSEP) encrypted card payment data
 * @param {PaymentBuilderDependencies} deps - Dependencies
 * @returns {PaymentMethod | null} Multipass card payment method
 */
export const buildMultiPassCardPaymentData = (deps) => {
  const { paymentType, firstName, lastName, tsepResponse, binCodesResponse, setErrors } = deps;

  const accountName = `${firstName?.trim()} ${lastName?.trim()}`.trim();
  const { encryptedCard, encryptedCSC } = getEncryptedCardData();

  // Guard: ensure all required TSEP encrypted fields are present
  if (!encryptedCard || !encryptedCSC) {
    setErrors((/** @type {string[]} */ prev) => [...prev, ENCRYPTION_ERROR_MESSAGE]);
    return null;
  }

  // Extract expiration date from TSEP response or DOM (with validation)
  let expirationDate = '';
  if (tsepResponse?.expirationDate && tsepResponse.expirationDate !== '__/____') {
    expirationDate = tsepResponse.expirationDate;
  } else {
    const datepickerDiv = document.getElementById('tsep-datepickerDiv');
    if (datepickerDiv) {
      const inputField = datepickerDiv.querySelector('input');
      const value = inputField?.value?.trim();
      const expirationDatePattern = /^\d{2}\/\d{4}$/; // MM/YYYY format
      // Validate format MM/YYYY
      if (value && value !== '__/____' && expirationDatePattern.test(value)) {
        expirationDate = value;
      }
    }
  }

  return {
    paymentType,
    accountName,
    encryptedCSC: encryptedCSC.trim(),
    encryptedCard: encryptedCard.trim(),
    expirationDate,
    binCodesResponse: binCodesResponse || {},
    cardNetwork: binCodesResponse?.card || tsepResponse?.cardType || '',
    maskedAccountNumber: '',
  };
};

/**
 * @description Build Paytrace encrypted card payment data
 * @async
 * @param {PaymentBuilderDependencies} deps - Dependencies
 * @returns {Promise<PaymentMethod | null>} Paytrace card payment method or null on encryption error
 */
export const buildPaytraceCardPaymentData = async (deps) => {
  const { paymentType, firstName, lastName, card, binCodesResponse, setErrors } = deps;

  const accountName = `${firstName?.trim()} ${lastName?.trim()}`.trim();

  // Submit encryption via Paytrace gateway
  try {
    const paytrace = /** @type {Window} */ (window).paytrace;
    if (!paytrace?.submitEncrypted) {
      throw new Error('Paytrace SDK not loaded');
    }
    await paytrace.submitEncrypted(PAYTRACE_FORM_SELECTOR);
  } catch (encryptionError) {
    console.error('Card encryption error:', encryptionError);
    setErrors((/** @type {string[]} */ prev) => [...prev, ENCRYPTION_ERROR_MESSAGE]);
    return null;
  }

  const { encryptedCard, encryptedCSC } = getEncryptedCardData();

  // Guard: ensure all required Paytrace encrypted fields are present
  if (!encryptedCard || !encryptedCSC) {
    setErrors((/** @type {string[]} */ prev) => [...prev, ENCRYPTION_ERROR_MESSAGE]);
    return null;
  }

  return {
    paymentType,
    accountName,
    encryptedCSC: encryptedCSC || '',
    encryptedCard: encryptedCard || '',
    expirationDate: card.cardExpiryDate || '',
    binCodesResponse: binCodesResponse || {},
    cardNetwork: binCodesResponse?.card || '',
    maskedAccountNumber: '',
  };
};

/**
 * @description Build ACH bank transfer payment data
 * @param {PaymentBuilderDependencies} deps - Dependencies
 * @returns {PaymentMethod | null} ACH payment method
 */
export const buildACHPaymentData = (deps) => {
  const { paymentType, firstName, lastName, ach, setErrors } = deps;

  const accountName = `${firstName?.trim()} ${lastName?.trim()}`.trim();

  // Guard: ensure required ACH fields are present
  if (!ach.accountNumber?.trim() || !ach.routingNumber?.trim()) {
    setErrors((/** @type {string[]} */ prev) => [
      ...prev,
      'ACH payment data is missing required fields. Please fill in all required fields.',
    ]);
    return null;
  }

  return {
    paymentType,
    accountName: ach.accountName || accountName,
    accountNumber: ach.accountNumber || '',
    routingNumber: ach.routingNumber || '',
    accountType: ach.bankAccountType || '',
    bankName: ach.bankName || '',
    maskedAccountNumber: '',
  };
};

/**
 * @description Build complete payment data object for submission
 * Orchestrates specialized payment method builders based on active tab and gateway
 * Conforms to PaymentDetailsSchema on backend (commonSchemas.js)
 *
 * @async
 * @param {PaymentBuilderDependencies} deps - Dependencies
 * @returns {Promise<PaymentData|null>} Payment form data with proper paymentMethod structure, or null on error
 */
export const buildPaymentData = async (deps) => {
  const {
    paymentGateway,
    activeTab,
    billingAddress,
    shippingAddress,
    card,
    ach,
    calculationResult,
    tsepResponse,
    setErrors,
  } = deps;

  let paymentMethod;

  let valueToMask;
  if (activeTab === PAYMENT_TABS.CARD) {
    valueToMask = card?.cardNumber || tsepResponse?.maskedCardNumber || '';
  } else {
    valueToMask = ach?.accountNumber || '';
  }

  const maskedAccountNumber = maskPaymentValue(valueToMask || '');

  if (activeTab === PAYMENT_TABS.CARD) {
    if (paymentGateway === commonStrings.multipass) {
      paymentMethod = buildMultiPassCardPaymentData(deps);
    } else {
      // Paytrace gateway - async encryption required
      paymentMethod = await buildPaytraceCardPaymentData(deps);
    }
  } else {
    // ACH payment
    paymentMethod = buildACHPaymentData(deps);
  }

  if (paymentMethod) {
    paymentMethod.maskedAccountNumber = maskedAccountNumber;
  } else {
    setErrors((/** @type {string[]} */ prev) => [
      ...prev,
      'Failed to build payment method data. Please check your input and try again.',
    ]);
    return null;
  }

  return {
    paymentMethod,
    billingAddress,
    shippingAddress,
    calculationResult,
  };
};
